import React, { useState, useCallback, useMemo } from "react";
import { Link } from "./link";
import { normalizeRelativePath } from "../utility/paths";
import Download from "../icons/download-arrow.svg";
import classNames from "classnames";
import DeclarationModal from "./declaration-modal";

const DOC_BASE_URL = `https://docgenerator.candy.it/docgenerator`;

const getDeclarationOfConformity = async (sku, region) => {
  const response = await fetch(`/headless-api/libretti?sku=${sku}&origin=certs`, {
    method: "GET",
  });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`);
  }
  const data = await response.json();
  return data?.data?.length
    ? data.data.find(
        (dec) =>
          dec?.ca4_CHG_doc_subtype?.includes(region) && dec?.ca4_CHG_doc_type === "DECLARATION"
      )?.ca4_CHG_pc_url
    : null;
};

const DeclarationButton = ({ label, region, sku }) => {
  const [errorPopUp, setErrorPopUp] = useState("");
  const handleOnClick = async () => {
    try {
      const url = await getDeclarationOfConformity(sku, region);

      if (url) {
        window.open(url, "_blank");
      } else {
        setErrorPopUp(null);
      }
    } catch (error) {
      setErrorPopUp(undefined);
    }
  };

  return (
    <>
      <li className="box-download__item">
        <button
          className="box-download__link"
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleOnClick}
        >
          <Download className="icon-svg" />
          {label}
        </button>
      </li>
      <DeclarationModal
        show={errorPopUp === null || errorPopUp === undefined}
        onHide={() => setErrorPopUp("")}
        type={errorPopUp}
      />
    </>
  );
};

const DocumentazioneProdotto = ({
  data: {
    key_prod: productId,
    manuals,
    declarationOfConformity,
    lineId,
    pageLine: { lineConfigurationFieldSet },
    energyLabelCav,
  },
  labelManagerLabels,
  siteMetadata,
  config,
  isProduct,
}) => {
  const {
    catalogueLinkFieldSet,
    isProductSheet,
    isNewEnergyLabelEu,
    isFicheDeProduit,
    isNewFicheDeProduit,
    isNewEnergyLabelUk,
    isDeclarationOfConformity,
    isDeclarationOfConformityUk,
  } = lineConfigurationFieldSet;

  const LANG = useMemo(
    () => siteMetadata.derivedLang || siteMetadata.priceLanguage.split("-")[0].toUpperCase(),
    [siteMetadata]
  );
  const MERCATO = useMemo(() => siteMetadata.babelPrefix.split("-")[1], [siteMetadata.babelPrefix]);

  const manual = useMemo(() => {
    const defaultUserManual =
      manuals &&
      manuals.find(
        (item) =>
          item.lang === LANG && [0, 50700].includes(item.type) && item.filename?.startsWith("http")
      );
    const fallbackUserManual =
      manuals &&
      manuals.find(
        (item) =>
          item.lang === LANG && [50709].includes(item.type) && item.filename?.startsWith("http")
      );
    return defaultUserManual ? defaultUserManual : fallbackUserManual;
  }, [manuals, LANG]);

  const manualType50712 = useMemo(() => {
    const manual =
      manuals &&
      manuals.find(
        (item) =>
          item.lang === LANG && [0, 50712].includes(item.type) && item.filename?.startsWith("http")
      );
    return manual ? manual : null;
  }, [manuals, LANG]);

  const installationGuide = useMemo(() => {
    const manual =
      manuals &&
      manuals.find(
        (item) =>
          item.lang === LANG && [50708].includes(item.type) && item.filename?.startsWith("http")
      );

    return manual ? manual : null;
  }, [manuals, LANG]);

  const [boxDoc, setboxDoc] = useState(false);
  const toggleBoxDoc = useCallback(() => setboxDoc((value) => !value), []);

  const oldLines = config.haierStEcommLines.productLine;

  const energyLabelUrl = useMemo(() => {
    if (!lineId) return null;
    const isOld = oldLines.includes(`${lineId}`);
    return `${DOC_BASE_URL}/${isOld ? `energylabel?lan=${LANG}` : "newenergylabel?"}`;
  }, [lineId, LANG, oldLines]);

  const energyLabelLabel = useMemo(() => {
    if (!lineId) return null;
    const isOld = oldLines.includes(`${lineId}`);
    return labelManagerLabels[isOld ? "energyLabelEl" : "newEnergyLabel"];
  }, [labelManagerLabels, lineId, oldLines]);

  const check8815 =
    (oldLines.includes(`${lineId}`) || lineId === 666 || lineId === 17) &&
    manuals?.find((item) => item?.type === 8815 && item?.filename?.startsWith("http"));
  const check8816 =
    (!oldLines.includes(`${lineId}`) || lineId === 666 || lineId === 17) &&
    manuals?.find((item) => item?.type === 8816 && item?.filename?.startsWith("http"));
  const check8825 =
    (oldLines.includes(`${lineId}`) || lineId === 666 || lineId === 17) &&
    manuals?.find((item) => item?.type === 8825 && item?.filename?.startsWith("http"));
  const check8826 =
    (!oldLines.includes(`${lineId}`) || lineId === 666 || lineId === 17) &&
    manuals?.find((item) => item?.type === 8826 && item?.filename?.startsWith("http"));
  const manual8815 = manuals?.find(
    (item) => item?.type === 8815 && item?.filename?.startsWith("http")
  )?.filename;
  const manual8816 = manuals?.find(
    (item) => item?.type === 8816 && item?.filename?.startsWith("http")
  )?.filename;
  const manual8825 = manuals?.find(
    (item) => item?.type === 8825 && item?.filename?.startsWith("http")
  )?.filename;
  const manual8826 = manuals?.find(
    (item) => item?.type === 8826 && item?.filename?.startsWith("http")
  )?.filename;

  const energyLabelCavLink = useMemo(() => {
    if (!(energyLabelCav > 0) && check8815 && check8816 && (lineId === 666 || lineId === 17)) {
      return [
        {
          label: energyLabelLabel,
          url: manual8815,
        },
        {
          label: energyLabelLabel,
          url: manual8816,
        },
      ];
    } else if (check8815 && !(energyLabelCav > 0)) {
      return [
        {
          label: energyLabelLabel,
          url: manual8815,
        },
      ];
    } else if (check8816 && !(energyLabelCav > 0)) {
      return [
        {
          label: energyLabelLabel,
          url: manual8816,
        },
      ];
    } else if (energyLabelCav > 0) {
      const res = [];
      for (let index = 0; index < energyLabelCav; index++) {
        res.push({
          label: `${
            labelManagerLabels?.energyLabelCavity
              ? labelManagerLabels.energyLabelCavity
              : energyLabelLabel
          } ${index + 1}`,
          url: check8815
            ? manual8815
            : check8816
            ? manual8816
            : `${energyLabelUrl}&cav=${index + 1}`,
        });
      }
      return res;
    } else {
      return [{ label: energyLabelLabel, url: energyLabelUrl }];
    }
  }, [
    energyLabelCav,
    energyLabelLabel,
    energyLabelUrl,
    labelManagerLabels.energyLabelCavity,
    manual8815,
    manual8816,
    check8815,
    check8816,
    lineId,
  ]);

  // const ficheDeProduitUrl = useMemo(() => {
  //   if (!lineId) return null;
  //   const oldLines = config.productLine.map((item) => item.value[0]);
  //   const isOld = oldLines.includes(`${lineId}`);
  //   return isOld ? `${DOC_BASE_URL}/fichedeproduit?lan=${LANG}` : null;
  // }, [LANG, lineId, config]);

  const showTitle =
    manual ||
    installationGuide ||
    manualType50712 ||
    (isProductSheet && !!isProductSheet?.length) ||
    (catalogueLinkFieldSet &&
      catalogueLinkFieldSet.catalogueLink &&
      catalogueLinkFieldSet.isCatalogue &&
      !!catalogueLinkFieldSet.isCatalogue?.length) ||
    (isNewEnergyLabelEu && !!isNewEnergyLabelEu?.length) ||
    (isNewEnergyLabelUk && !!isNewEnergyLabelUk?.length) ||
    (isFicheDeProduit && !!isFicheDeProduit?.length) ||
    (isNewFicheDeProduit && !!isNewFicheDeProduit?.length) ||
    (isDeclarationOfConformity &&
      !!isDeclarationOfConformity?.length &&
      declarationOfConformity?.eu === "true") ||
    (isDeclarationOfConformityUk &&
      !!isDeclarationOfConformityUk?.length &&
      declarationOfConformity?.uk === "true");

  return (
    <section className="section">
      <div className="container-fluid">
        <div className="row">
          <div className="col-xl-8 offset-xl-2 col-lg-10 offset-lg-1">
            <div
              className={`box-download pt-2${
                boxDoc
                  ? " box-download--open"
                  : isProduct
                  ? " box-download--open box-download--pdp"
                  : ""
              }`}
            >
              {showTitle && (
                <button
                  onClick={toggleBoxDoc}
                  className={classNames({
                    "box-download__btn d-block d-md-none w-100 text-left": !isProduct,
                    "d-none": isProduct,
                  })}
                >
                  {labelManagerLabels.documents}
                </button>
              )}
              {showTitle && (
                <h2
                  className={classNames({
                    "section__title mb-4 d-none d-md-block": !isProduct,
                    "section__title mb-4 d-block": isProduct,
                  })}
                >
                  {labelManagerLabels.documents}
                </h2>
              )}
              <div
                className={classNames("box-download__wrapper py-md-3", {
                  "box-download__wrapper--pdp": isProduct,
                })}
              >
                <ul className="box-download__list d-flex flex-column flex-md-row justify-content-around align-self-center">
                  {manual && (
                    <li className="box-download__item">
                      <a
                        className="box-download__link"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={manual.filename}
                      >
                        <Download className="icon-svg" />
                        {labelManagerLabels.instructionsManual}
                      </a>
                    </li>
                  )}
                  {installationGuide && (
                    <li className="box-download__item">
                      <a
                        className="box-download__link"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={installationGuide.filename}
                      >
                        <Download className="icon-svg" />
                        {labelManagerLabels.installationManual}
                      </a>
                    </li>
                  )}
                  {isProductSheet && !!isProductSheet?.length && (
                    <li className="box-download__item">
                      <a
                        className="box-download__link"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`${DOC_BASE_URL}/productdetails?lan=${LANG}&sku=${productId}&mkt=${MERCATO}`}
                      >
                        <Download className="icon-svg" />
                        {labelManagerLabels.productSheet}
                      </a>
                    </li>
                  )}
                  {catalogueLinkFieldSet &&
                    catalogueLinkFieldSet.catalogueLink &&
                    catalogueLinkFieldSet &&
                    catalogueLinkFieldSet.isCatalogue &&
                    !!catalogueLinkFieldSet.isCatalogue?.length && (
                      <li className="box-download__item">
                        <Link
                          className="box-download__link"
                          to={normalizeRelativePath(catalogueLinkFieldSet.catalogueLink)}
                        >
                          <Download className="icon-svg" />
                          {labelManagerLabels.catalogue}
                        </Link>
                      </li>
                    )}
                  {isNewEnergyLabelEu &&
                    !!isNewEnergyLabelEu?.length &&
                    energyLabelCavLink.map((item, i) =>
                      lineId !== 666 && lineId !== 17 ? (
                        <li className="box-download__item" key={i}>
                          <a
                            className="box-download__link"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={
                              check8815 || check8816
                                ? item?.url
                                : `${item.url}&mkt=EU&sku=${productId}`
                            }
                          >
                            <Download className="icon-svg" />
                            {item.label}
                          </a>
                        </li>
                      ) : check8815 || check8816 ? (
                        <li className="box-download__item" key={i}>
                          <a
                            className="box-download__link"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={item?.url}
                          >
                            <Download className="icon-svg" />
                            {item.label}
                          </a>
                        </li>
                      ) : null
                    )}

                  {isNewEnergyLabelUk &&
                  !!isNewEnergyLabelUk?.length &&
                  lineId !== 666 &&
                  lineId !== 17 ? (
                    <li className="box-download__item">
                      <a
                        className="box-download__link"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={
                          check8825
                            ? manual8825
                            : check8826
                            ? manual8826
                            : `${energyLabelUrl}&mkt=UK&sku=${productId}`
                        }
                      >
                        <Download className="icon-svg" />
                        {labelManagerLabels.energyLabelUk}
                      </a>
                    </li>
                  ) : isNewEnergyLabelUk &&
                    !!isNewEnergyLabelUk?.length &&
                    (check8825 || check8826) ? (
                    <li className="box-download__item">
                      <a
                        className="box-download__link"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={check8825 ? manual8825 : manual8826}
                      >
                        <Download className="icon-svg" />
                        {labelManagerLabels.energyLabelUk}
                      </a>
                    </li>
                  ) : null}

                  {isFicheDeProduit &&
                  !!isFicheDeProduit?.length &&
                  manuals?.find(
                    (item) =>
                      item?.lang === LANG &&
                      item?.type === 771 &&
                      item?.filename?.startsWith("http")
                  ) ? (
                    <li className="box-download__item">
                      <a
                        className="box-download__link"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={
                          manuals?.find(
                            (item) =>
                              item?.lang === LANG &&
                              item?.type === 771 &&
                              item?.filename?.startsWith("http")
                          )?.filename
                        }
                      >
                        <Download className="icon-svg" />
                        {labelManagerLabels.ficheDeProduit}
                      </a>
                    </li>
                  ) : null}
                  {isNewFicheDeProduit &&
                    !!isNewFicheDeProduit?.length &&
                    manuals?.find(
                      (item) =>
                        item?.lang === LANG &&
                        item?.type === 772 &&
                        item?.filename?.startsWith("http")
                    ) && (
                      <li className="box-download__item">
                        <a
                          className="box-download__link"
                          target="_blank"
                          rel="noopener noreferrer"
                          href={
                            manuals?.find(
                              (item) =>
                                item?.lang === LANG &&
                                item?.type === 772 &&
                                item?.filename?.startsWith("http")
                            )?.filename
                          }
                        >
                          <Download className="icon-svg" />
                          {labelManagerLabels.newFicheDeProduit}
                        </a>
                      </li>
                    )}
                  {isDeclarationOfConformity &&
                    !!isDeclarationOfConformity?.length &&
                    declarationOfConformity?.eu === "true" && (
                      <DeclarationButton
                        label={labelManagerLabels.declarationOfConformity}
                        region="EU"
                        sku={productId}
                      />
                    )}
                  {isDeclarationOfConformityUk &&
                    isDeclarationOfConformity &&
                    !!isDeclarationOfConformityUk?.length &&
                    declarationOfConformity?.uk === "true" && (
                      <DeclarationButton
                        label={labelManagerLabels.declarationOfConformityUk}
                        region="UK"
                        sku={productId}
                      />
                    )}
                  {manualType50712 && (
                    <li className="box-download__item">
                      <a
                        className="box-download__link"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={manualType50712.filename}
                      >
                        <Download className="icon-svg" />
                        {labelManagerLabels.manualType50712}
                      </a>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DocumentazioneProdotto;
